import React, { useState, useEffect } from 'react';
import axios from '../utils/axios';
import './TrialBalance.css';

function TrialBalance() {
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchTrialBalance();
  }, []);

  const fetchTrialBalance = async () => {
    setError('');
    setLoading(true);

    try {
      // Get connection status to retrieve organizationId (realmId)
      const statusResponse = await axios.get('/auth/status');
      const organizations = statusResponse.data.organizations || [];
      
      if (organizations.length === 0) {
        setError('No QuickBooks organization found. Please reconnect to QuickBooks.');
        setLoading(false);
        return;
      }

      // Use the first organization's realmId as organizationId
      const organizationId = organizations[0].realmId;

      if (!organizationId) {
        setError('Organization ID not found. Please reconnect to QuickBooks.');
        setLoading(false);
        return;
      }

      const params = new URLSearchParams();
      params.append('organizationId', organizationId);

      const response = await axios.get(`/api/accounting/trial-balance?${params.toString()}`);
      setReportData(response.data.data || response.data);
    } catch (err) {
      console.error('Error fetching trial balance:', err);
      setError(err.response?.data?.error || 'Failed to fetch trial balance report');
    } finally {
      setLoading(false);
    }
  };

  const getKeyMetrics = () => {
    if (!reportData?.Rows?.Row) return null;

    const rows = reportData.Rows.Row;
    
    // Find the summary row (last row with type: "Section" and group: "GrandTotal")
    const summaryRow = rows.find(row => row.type === 'Section' && row.group === 'GrandTotal');
    
    if (summaryRow?.Summary?.ColData) {
      const totalDebits = summaryRow.Summary.ColData[1]?.value || '0.00';
      const totalCredits = summaryRow.Summary.ColData[2]?.value || '0.00';
      return { totalDebits, totalCredits };
    }

    return { totalDebits: '0.00', totalCredits: '0.00' };
  };

  const metrics = reportData ? getKeyMetrics() : null;

  const renderAccountRow = (row, idx) => {
    // Skip summary rows (they have type: "Section")
    if (row.type === 'Section' && row.Summary) {
      return (
        <div key={idx} className="tb-summary-row">
          <div className="tb-summary-cell account-name">
            <strong>{row.Summary.ColData[0]?.value || ''}</strong>
          </div>
          <div className="tb-summary-cell debit-amount">
            <strong>${row.Summary.ColData[1]?.value || '0.00'}</strong>
          </div>
          <div className="tb-summary-cell credit-amount">
            <strong>${row.Summary.ColData[2]?.value || '0.00'}</strong>
          </div>
        </div>
      );
    }

    // Regular account rows
    if (row.ColData) {
      const accountName = row.ColData[0]?.value || '';
      const debitAmount = row.ColData[1]?.value || '';
      const creditAmount = row.ColData[2]?.value || '';

      return (
        <div key={idx} className="tb-account-row">
          <div className="tb-account-cell account-name">{accountName}</div>
          <div className="tb-account-cell debit-amount">
            {debitAmount ? `$${debitAmount}` : '-'}
          </div>
          <div className="tb-account-cell credit-amount">
            {creditAmount ? `$${creditAmount}` : '-'}
          </div>
        </div>
      );
    }

    return null;
  };

  if (loading) {
    return (
      <div className="trial-balance-container">
        <div className="loading-spinner">Loading Trial Balance...</div>
      </div>
    );
  }

  return (
    <div className="trial-balance-container">
      <h2>⚖️ Trial Balance Report</h2>
      
      <button onClick={fetchTrialBalance} className="btn btn-secondary refresh-btn" disabled={loading}>
        {loading ? 'Refreshing...' : '🔄 Refresh'}
      </button>

      {error && (
        <div className="error-message">
          <span className="error-icon">⚠️</span>
          {error}
        </div>
      )}

      {metrics && (
        <div className="key-metrics">
          <div className="metric-card debit-card">
            <div className="metric-label">Total Debits</div>
            <div className="metric-value">${metrics.totalDebits}</div>
          </div>
          <div className="metric-card credit-card">
            <div className="metric-label">Total Credits</div>
            <div className="metric-value">${metrics.totalCredits}</div>
          </div>
          <div className={`metric-card balance-card ${metrics.totalDebits === metrics.totalCredits ? 'balanced' : 'unbalanced'}`}>
            <div className="metric-label">Status</div>
            <div className="metric-value">
              {metrics.totalDebits === metrics.totalCredits ? '✅ Balanced' : '⚠️ Unbalanced'}
            </div>
          </div>
        </div>
      )}

      {reportData && (
        <div className="tb-report">
          <div className="report-header">
            <h3>{reportData.Header?.ReportName}</h3>
            <div className="report-info">
              <p><strong>Period:</strong> {reportData.Header?.StartPeriod} to {reportData.Header?.EndPeriod}</p>
              <p><strong>Basis:</strong> {reportData.Header?.ReportBasis}</p>
              <p><strong>Currency:</strong> {reportData.Header?.Currency}</p>
            </div>
          </div>

          <div className="tb-report-table">
            <div className="tb-table-header">
              <div className="tb-header-cell account-name">Account</div>
              <div className="tb-header-cell debit-amount">Debit</div>
              <div className="tb-header-cell credit-amount">Credit</div>
            </div>

            <div className="tb-table-body">
              {reportData.Rows?.Row && (
                Array.isArray(reportData.Rows.Row)
                  ? reportData.Rows.Row.map((row, idx) => renderAccountRow(row, idx))
                  : renderAccountRow(reportData.Rows.Row, 0)
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default TrialBalance;
